// SVG back end for the editor's preview: paint primitives → an SVG string in mm units. The
// barcode drawing is injected (bwip-js in the browser, nothing in tests), so this file runs
// anywhere. Element ids are carried as data attributes when the caller maps items to elements.
import { textWidthMm } from "./metrics.js";
const PT = 72 / 25.4;
const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
export function pageToSvg(page, opts = {}) {
    const parts = [];
    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${page.width} ${page.height}" width="${page.width}mm" height="${page.height}mm" font-family="Helvetica, Arial, sans-serif">`);
    parts.push(`<rect x="0" y="0" width="${page.width}" height="${page.height}" fill="#fff"/>`);
    if (opts.grid) {
        const g = opts.grid;
        let path = "";
        for (let x = g; x < page.width; x += g)
            path += `M${x} 0V${page.height}`;
        for (let y = g; y < page.height; y += g)
            path += `M0 ${y}H${page.width}`;
        parts.push(`<path d="${path}" stroke="#e3e8ef" stroke-width="0.08" fill="none"/>`);
    }
    for (const it of page.items)
        parts.push(paintToSvg(it, opts));
    parts.push("</svg>");
    return parts.join("");
}
export function paintToSvg(p, opts) {
    switch (p.kind) {
        case "group": {
            const t = p.rotation === 0 ? `translate(${p.x} ${p.y})` : p.rotation === 90 ? `translate(${p.x + p.w} ${p.y}) rotate(90)` : p.rotation === 180 ? `translate(${p.x + p.w} ${p.y + p.h}) rotate(180)` : `translate(${p.x} ${p.y + p.h}) rotate(270)`;
            return `<g transform="${t}">${p.items.map((c) => paintToSvg(c, opts)).join("")}</g>`;
        }
        case "rect":
            return `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" rx="${p.radius}" fill="${p.fill ? "#000" : "none"}" stroke="${p.fill || p.thickness <= 0 ? "none" : "#000"}" stroke-width="${p.thickness}"/>`;
        case "line":
            return `<line x1="${p.x1}" y1="${p.y1}" x2="${p.x2}" y2="${p.y2}" stroke="#000" stroke-width="${p.thickness}"/>`;
        case "text": {
            if (!p.text)
                return "";
            const em = p.size / PT;
            const tw = textWidthMm(p.text, p.font, p.size) * p.scaleX;
            const x = p.align === "left" ? p.x : p.align === "center" ? p.x + Math.max(0, (p.w - tw) / 2) : p.x + Math.max(0, p.w - tw);
            // Baseline ≈ 0.78 em below the top of the em box; the line box centres the em box.
            const y = p.y + Math.max(0, (p.h - em) / 2) + em * 0.78;
            const family = p.font.family === "mono" ? "Courier New, Courier, monospace" : "Helvetica, Arial, sans-serif";
            const transform = p.scaleX !== 1 ? ` transform="translate(${x} ${y}) scale(${p.scaleX} 1)"` : "";
            const pos = p.scaleX !== 1 ? `x="0" y="0"` : `x="${x}" y="${y}"`;
            return `<text ${pos}${transform} font-size="${em}" font-family="${family}"${p.font.bold ? ' font-weight="700"' : ""}${p.font.italic ? ' font-style="italic"' : ""} fill="${p.inverse ? "#fff" : "#000"}" xml:space="preserve">${esc(p.text)}</text>`;
        }
        case "barcode": {
            const full = opts.barcode ? opts.barcode(p.symbology, p.data, p.w, p.h) : null;
            if (full) {
                // The callback returns a whole <svg>; keep its viewBox and content, size it to the box.
                const vb = /viewBox="([^"]+)"/.exec(full)?.[1] ?? `0 0 ${p.w} ${p.h}`;
                const inner = full.replace(/^[\s\S]*?<svg[^>]*>/, "").replace(/<\/svg>\s*$/, "");
                const square = /qr|datamatrix/.test(p.symbology);
                return `<svg x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" viewBox="${vb}" preserveAspectRatio="${square ? "xMidYMid meet" : "none"}">${inner}</svg>`;
            }
            // Placeholder: striped box with the symbology name.
            return `<g><rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" fill="url(#bars)" stroke="#888" stroke-width="0.2"/><text x="${p.x + p.w / 2}" y="${p.y + p.h / 2}" font-size="3" text-anchor="middle" fill="#555">${esc(p.symbology)}</text></g>`;
        }
        case "image":
            return `<image x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" href="${esc(p.src)}" preserveAspectRatio="xMidYMid meet"/>`;
    }
}
//# sourceMappingURL=svg-out.js.map